import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useTheme,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useGetMergedPublisherMetricsQuery } from "../../store/apis/publisherMetricsApi";
import { components } from "../../types/api";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { exportPublisherMetricsToCSV } from "../../utils/exportPublisherMetricsUtils";
import { getCurrencySymbol } from "../../utils/currencyUtils";
import { getPlatformDisplayName } from "../../utils/iconUtils";
import {
  formatCurrency,
  formatNumber,
  formatPercentageNoMultiply,
} from "../../utils/formatUtils";

import OrbLoader from "../common/OrbLoader";
type Publisher = components["schemas"]["PublisherMetricsSummary"];
type Order = "asc" | "desc";

/**
 * Columnas ordenables de la tabla, en el orden en que se pintan.
 *
 * Se declaran como datos en vez de repetir el mismo bloque de TableCell +
 * TableSortLabel siete veces: la cabecera y el criterio de orden salen de aqui,
 * asi que anadir una columna es anadir una entrada.
 */
const COLUMNS: {
  field: keyof Publisher;
  labelKey: string;
  fallback: string;
}[] = [
  {
    field: "publisherName",
    labelKey: "campaigns.publisher",
    fallback: "Publisher",
  },
  { field: "platform", labelKey: "campaigns.platform", fallback: "Platform" },
  {
    field: "impressions",
    labelKey: "campaigns.impressions",
    fallback: "Impressions",
  },
  { field: "clicks", labelKey: "campaigns.clicks", fallback: "Clicks" },
  { field: "ctr", labelKey: "campaigns.ctr", fallback: "CTR" },
  { field: "cpm", labelKey: "campaigns.cpm", fallback: "CPM" },
  { field: "cost", labelKey: "campaigns.cost", fallback: "Cost" },
];

export interface PublisherMetricsSectionProps {
  /** Merge cuyas subcampanas agregan el desglose. */
  mergedCampaignId?: string;
  /** Nombre usado como base del fichero exportado. */
  campaignName?: string;
  currency?: string;
  /** Ventana de la campana, solo para la etiqueta de la cabecera. */
  startDate?: string;
  endDate?: string;
  /** Título de la sección; por defecto el genérico de la tabla. */
  title?: string;
}

/**
 * Desglose por site/dominio de una campana agrupada.
 *
 * Estaba embebido en el dashboard de NatAds, que era el unico que lo pedia.
 * DV360 empezo a poblar `PublisherMetrics` con sus propios sites, y el endpoint
 * `/publisher-metrics/merged/{id}` no filtra por plataforma, asi que servia tal
 * cual para los dos dashboards — solo hacia falta dejar de tenerlo pegado a uno.
 *
 * ## Sobre las cifras
 *
 * El desglose es una **foto de totales de vida** de la campana, no una serie
 * diaria: la ingesta borra y reescribe. Por eso el componente no acepta filtro
 * de fechas y las de la cabecera son solo la etiqueta del periodo de la
 * campana. Filtrar por fechas aqui daria la sensacion de recortar el periodo
 * sin recortarlo.
 */
const PublisherMetricsSection: React.FC<PublisherMetricsSectionProps> = ({
  mergedCampaignId,
  campaignName,
  currency,
  startDate,
  endDate,
  title,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<string>("impressions");

  const { data, error, isLoading } = useGetMergedPublisherMetricsQuery(
    { path: { internalCampaignMergeId: mergedCampaignId || "" } },
    {
      skip: !mergedCampaignId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  useEffect(() => {
    if (error) {
      dispatch(
        setToastMessage({
          message: t("campaigns.publisherMetricsError", {
            defaultValue: "Failed to load publisher metrics",
          }),
          severity: "error",
        })
      );
    }
  }, [error, dispatch, t]);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const publishers = data?.publishers;

  const sorted = useMemo(() => {
    if (!publishers) return [];
    return [...publishers].sort((a: Publisher, b: Publisher) => {
      const aVal = a[orderBy as keyof Publisher] ?? 0;
      const bVal = b[orderBy as keyof Publisher] ?? 0;
      if (bVal < aVal) return order === "asc" ? 1 : -1;
      if (bVal > aVal) return order === "asc" ? -1 : 1;
      return 0;
    });
  }, [publishers, order, orderBy]);

  const handleExport = () => {
    if (!publishers || publishers.length === 0) {
      dispatch(
        setToastMessage({
          message: t("common.noDataToExport", {
            defaultValue: "No data to export",
          }),
          severity: "warning",
        })
      );
      return;
    }

    exportPublisherMetricsToCSV(
      publishers,
      campaignName || "export",
      t,
      dispatch,
      getCurrencySymbol(currency || "EUR")
    );
  };

  if (!data) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title ??
              t("campaigns.publisherMetrics", {
                defaultValue: "Publisher Metrics",
              })}
          </Typography>
          {startDate && endDate && (
            <Box
              sx={{
                display: "flex",
                bgcolor: "primary.light",
                color: "primary.contrastText",
                borderRadius: 1,
                px: 1,
                py: 0.5,
                fontSize: "0.75rem",
                fontWeight: 500,
                alignItems: "center",
              }}
            >
              {`${startDate} - ${endDate}`}
            </Box>
          )}
        </Box>

        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          sx={{
            height: 36,
            textTransform: "none",
            fontWeight: 500,
            alignSelf: { xs: "flex-start", sm: "auto" },
          }}
          disabled={!publishers || publishers.length === 0}
        >
          {t("campaigns.exportPublisherMetrics", {
            defaultValue: "Export Data",
          })}
        </Button>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100px",
          }}
        >
          <OrbLoader size="medium" />
        </Box>
      ) : sorted.length > 0 ? (
        <TableContainer
          component={Paper}
          sx={{ borderRadius: 2, maxHeight: 600, overflow: "auto" }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 650 }}
            aria-label="publisher metrics table"
          >
            <TableHead sx={{ backgroundColor: theme.palette.grey[200] }}>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell key={column.field} sx={{ fontWeight: "bold" }}>
                    <TableSortLabel
                      active={orderBy === column.field}
                      direction={orderBy === column.field ? order : "asc"}
                      onClick={() => handleRequestSort(column.field)}
                    >
                      {t(column.labelKey, { defaultValue: column.fallback })}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((publisher: Publisher) => (
                <TableRow
                  key={publisher.publisherId}
                  hover
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    transition: "background-color 0.3s ease",
                    "&:hover": { backgroundColor: theme.palette.grey[100] },
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {publisher.publisherName}
                      </Typography>
                      {publisher.sectionName && (
                        <Typography variant="body2" color="text.secondary">
                          {publisher.sectionName}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {publisher.platform
                      ? getPlatformDisplayName(publisher.platform)
                      : t("common.notAvailable", { defaultValue: "N/A" })}
                  </TableCell>
                  <TableCell>{formatNumber(publisher.impressions)}</TableCell>
                  <TableCell>{formatNumber(publisher.clicks)}</TableCell>
                  <TableCell>
                    {formatPercentageNoMultiply(publisher.ctr)}
                  </TableCell>
                  <TableCell>
                    {formatCurrency(publisher.cpm, currency)}
                  </TableCell>
                  <TableCell>
                    {formatCurrency(publisher.cost, currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            {t("campaigns.noPublisherMetrics", {
              defaultValue: "No publisher metrics available",
            })}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default PublisherMetricsSection;
